import * as ngCore from '@angular/core';
import { AfterViewInit, Component, Inject, OnInit, viewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, Observable, Subject, combineLatest, merge } from 'rxjs';
import { distinctUntilChanged, filter, first, map, startWith } from 'rxjs/operators';
import { indicate } from 'src/app/operators';
import { ComponentPropertyViewModel, ComponentViewModel, EsraPipingAPIClient, EsraSearchFilterModel, SearchRequest } from 'src/app/shared/models/autogenerated-piping';
import { deserializeMaterialAstm, MATERIAL_ASTM_PROPERTY_NAME, MaterialAstmValue } from 'src/app/shared/models/material-astm.model';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';

function equalsIgnoreCase(val1: string, val2: string): boolean {
  return val1.trim().toUpperCase() === val2.trim().toUpperCase();
}

@Component({
    selector: 'app-component-add-edit',
    templateUrl: './component-add-edit.component.html',
    styleUrls: ['./component-add-edit.component.scss'],
    standalone: false
})
export class ComponentAddEditComponent implements OnInit, AfterViewInit {
  addFormGroup: UntypedFormGroup;
  filteredEnds: Observable<string[]>;
  filteredType: Observable<string[]>;
  filteredGroup: Observable<string[]>;
  filteredMaterial: Observable<string[]>;
  filteredASTM: Observable<string[]>;
  filteredCONST: Observable<string[]>;
  filteredSTD: Observable<string[]>;
  filteredRequirements: Observable<string[]>;

  filteredEndsInit = [''];
  filteredTypeInit = [];
  filteredGroupInit = [''];
  filteredCONSTInit = [''];
  filteredSTDInit = [''];
  filteredRequirementsInit: string[] = [];

  searching$ = new BehaviorSubject<boolean>(false);
  private typeOptions$ = new BehaviorSubject<string[]>([]);
  private requirementsOptions$ = new BehaviorSubject<string[]>([]);
  /** Valid Material/ASTM combinations for the selected group; drives the cross-filtering of both autocompletes. */
  private materialAstmPairs$ = new BehaviorSubject<MaterialAstmValue[]>([]);
  private materialAstmPairs: MaterialAstmValue[] = [];
  private groupOpen = new Subject<void>;
  private endsOpen = new Subject<void>;

  readonly GROUPS_WITH_DESCRIPTION_DROPDOWN = ['gaskets', 'misc', 'bolts'];
  descriptionOptions: ComponentPropertyViewModel[] = [];

  groupTrigger = viewChild(MatAutocompleteTrigger);



  componentModel: ComponentViewModel;
  isEditMode = false;
  title = 'create new component';
  selectedGroup: string = null;
  visibleFieldNames: Set<string> = new Set();
  private canonicalPropertyNamesByField: Map<string, string> = new Map<string, string>();
  private lastGroupSelection: string | null = null;

  genericAutocompleteErrorMessage

  constructor(public dialogRef: MatDialogRef<ComponentAddEditComponent>,
    private _esraPipingAPIClient: EsraPipingAPIClient,
    private _formBuilder: UntypedFormBuilder,
    private _snackBarService: SnackBarService,
    @Inject(MAT_DIALOG_DATA) public _componentModel: any
  ) { }

  ngOnInit(): void {
    this.initFormGroup();
    this.loadModelData();

    const searchRequest = new SearchRequest();
    searchRequest.page = 0;
    searchRequest.perPage = 0;
    searchRequest.mainSearch = "*";

    this._esraPipingAPIClient.componentCatalogSearch(false, searchRequest)
      .pipe(
        indicate(this.searching$),
        first()
      )
      .subscribe(
        {
          next: (searchResponse) => {
            for (const filter of searchResponse.searchFilters) {
              this.buildFilterOptions(filter);
            }
            this.initAutocompleteFiledValues();
          },
          error: (err) => console.log('Error: ' + err)
        }
      );
  }

  ngAfterViewInit(): void {
    const trigger = this.groupTrigger();
    if (!trigger) return;
    trigger.autocomplete.opened.subscribe(() => this.groupOpen.next());
    // Material dialogs auto-focus the first focusable input, which fires handleFocus()
    // on the group autocomplete trigger and opens the panel before the user has interacted.
    // Subscribe to the first open event and immediately close the panel to suppress this.
    // first() auto-completes after one emission so subsequent user-triggered opens are unaffected.
    if (this.isEditMode && this.componentModel?.group) {
      trigger.autocomplete.opened.pipe(first()).subscribe(() => trigger.closePanel());
    }
  }

  loadModelData(): void {
    const sourceModel: Record<string, unknown> = (this._componentModel ?? {}) as Record<string, unknown>;
    this.componentModel = { ...(this._componentModel ?? {}) };

    const endValue: string | undefined = (sourceModel['end'] ?? sourceModel['End'] ?? sourceModel['ends'] ?? sourceModel['Ends']) as string | undefined;
    const specialRequirementsValue: string | null | undefined =
      (sourceModel['specialRequirements'] ?? sourceModel['SpecialRequirements']) as string | null | undefined;

    if (!this.componentModel.end && endValue) {
      this.componentModel.end = endValue;
    }

    if ((this.componentModel.specialRequirements === undefined || this.componentModel.specialRequirements === null) &&
      specialRequirementsValue !== undefined) {
      this.componentModel.specialRequirements = specialRequirementsValue;
    }

    if (this.componentModel?.componentId) {
      this.isEditMode = true;
      this.title = "Edit component";
    }
  }

  private buildFilterOptions(filter: EsraSearchFilterModel): void{
    const filterPropertyName = filter.filterPropertyName;

    if (equalsIgnoreCase(filterPropertyName, 'ends')) {
      this.filteredEndsInit = this.filteredEndsInit.concat(filter.options);
      return;
    }

    if (equalsIgnoreCase(filterPropertyName, 'type')) {
      this.filteredTypeInit = filter.options;
      return;
    }

    // Material/ASTM options are intentionally not taken from the search facets: they must come
    // from the selected group's MaterialAstm pairs so the two fields can narrow each other.

    if (equalsIgnoreCase(filterPropertyName, 'construction')) {
      this.filteredCONSTInit = this.filteredCONSTInit.concat(filter.options);
      return;
    }

    if (equalsIgnoreCase(filterPropertyName, 'designstandard')) {
      this.filteredSTDInit = this.filteredSTDInit.concat(filter.options);
      return;
    }

    if (equalsIgnoreCase(filterPropertyName.replace(/\s/g, ''), 'SpecialRequirements')) {
      this.filteredRequirementsInit = this.filteredRequirementsInit.concat(filter.options);
      return;
    }

    if (equalsIgnoreCase(filterPropertyName, 'group')) {
      this.filteredGroupInit = filter.options;
    }

  }

  private initFormGroup() {
    this.addFormGroup = this._formBuilder.group({
      description: [''],
      ends: [''],
      type: ['', Validators.required],
      group: ['', Validators.required],
      material: [''],
      astm: [''],
      const: [''],
      design: [''],
      requirements: [''],
    });

    this.addFormGroup.get('description').disable();
  }

  private initAutocompleteFiledValues() {
  this.addFormGroup.get('ends').setValidators([this.autocompleteStringValidator(this.filteredEndsInit)]);
  this.addFormGroup.get('type').setValidators([this.autocompleteStringValidator(this.filteredTypeInit), Validators.required]);
  this.addFormGroup.get('group').setValidators([this.autocompleteStringValidator(this.filteredGroupInit), Validators.required]);
  this.addFormGroup.get('material').setValidators([this.materialAstmPairValidator(pair => pair.astm, pair => pair.material, 'astm')]);
  this.addFormGroup.get('astm').setValidators([this.materialAstmPairValidator(pair => pair.material, pair => pair.astm, 'material')]);
  this.addFormGroup.get('const').setValidators([this.autocompleteStringValidator(this.filteredCONSTInit)]);
  this.addFormGroup.get('design').setValidators([this.autocompleteStringValidator(this.filteredSTDInit)]);

  const endsControl: AbstractControl = this.addFormGroup.get('ends');
  this.filteredEnds = merge(
    endsControl.valueChanges.pipe(map((value: string) => value ?? '')),
    this.endsOpen.pipe(map(() => ''))
  ).pipe(
    startWith(''),
    map((value: string) => this._filterOptions(value, this.filteredEndsInit))
  );

  this.filteredType = combineLatest([
    this.addFormGroup.get('type').valueChanges.pipe(startWith('')),
    this.typeOptions$
  ]).pipe(
    map(([value, options]) => this._filterOptions(value ?? '', options))
  );

  const groupContrl = this.addFormGroup.get('group');

  this.filteredGroup = merge(
    groupContrl.valueChanges.pipe(map(v => (typeof v === 'string' ? v : ''))),
    this.groupOpen.pipe(map(() => ''))
  ).pipe(
    startWith(''),
    map(value => this._filterOptions(value, this.filteredGroupInit))
  );


  this.filteredMaterial = combineLatest([
    this.addFormGroup.get('material').valueChanges.pipe(startWith('')),
    this.addFormGroup.get('astm').valueChanges.pipe(startWith('')),
    this.materialAstmPairs$
  ]).pipe(
    map(([typedMaterial, selectedAstm, pairs]) =>
      this._filterOptions(typedMaterial ?? '', this.materialOptionsFor(pairs, selectedAstm)))
  );
  this.filteredASTM = combineLatest([
    this.addFormGroup.get('astm').valueChanges.pipe(startWith('')),
    this.addFormGroup.get('material').valueChanges.pipe(startWith('')),
    this.materialAstmPairs$
  ]).pipe(
    map(([typedAstm, selectedMaterial, pairs]) =>
      this._filterOptions(typedAstm ?? '', this.astmOptionsFor(pairs, selectedMaterial)))
  );

  this.addFormGroup.get('material').valueChanges.subscribe(() => this.revalidateMaterialAstm());
  this.addFormGroup.get('astm').valueChanges.subscribe(() => this.revalidateMaterialAstm());

  this.filteredCONST = this.addFormGroup.get('const').valueChanges.pipe(
    startWith(''),
    map(value => this._filterOptions(value, this.filteredCONSTInit))
  );
  this.filteredSTD = this.addFormGroup.get('design').valueChanges.pipe(
    startWith(''),
    map(value => this._filterOptions(value, this.filteredSTDInit))
  );
  this.filteredRequirements = combineLatest([
    this.addFormGroup.get('requirements').valueChanges.pipe(startWith('')),
    this.requirementsOptions$
  ]).pipe(
    map(([value, options]) => this._filterOptions(value ?? '', options))
  );

  this.addFormGroup.get('group').valueChanges.pipe(
    distinctUntilChanged(),
    filter(val => this.filteredGroupInit.includes(val))
  ).subscribe(group => {
      const isFirstGroupSelection: boolean = this.lastGroupSelection === null;
      const hasGroupChanged: boolean = !isFirstGroupSelection && this.lastGroupSelection !== group;
      this.lastGroupSelection = group;

      if (!this.isEditMode || hasGroupChanged) {
        this.clearComponentPropertyInputsForGroupChange();
      }

    this.loadVisibleFieldsForGroup(group);
    this.loadTypesForGroup(group);
    this.addFormGroup.get('type').setValue('');
    this.addFormGroup.get('requirements').setValue('');

    const descCtrl = this.addFormGroup.get('description');
    if (this.GROUPS_WITH_DESCRIPTION_DROPDOWN.includes(group.toLowerCase())) {
      this.loadDescriptionsForGroup(group);
      descCtrl.enable();
      descCtrl.clearValidators();
    } else {
      this.descriptionOptions = [];
      descCtrl.setValue('');
      descCtrl.disable();
      descCtrl.clearValidators();
    }
    descCtrl.updateValueAndValidity();
  });

  if (this.isEditMode && this.componentModel.group) {
    this.loadVisibleFieldsForGroup(this.componentModel.group);
    this.loadTypesForGroup(this.componentModel.group);

    if (this.GROUPS_WITH_DESCRIPTION_DROPDOWN.includes(this.componentModel.group?.toLowerCase())) {
      this.loadDescriptionsForGroup(this.componentModel.group);
      const descCtrl = this.addFormGroup.get('description');
      descCtrl.enable();
      descCtrl.clearValidators();
      descCtrl.updateValueAndValidity();
    }
  }
}
  private readonly FIELD_KEY_MAP: Record<string, string> = {
    'schrat': 'schrat',
    'sch1rat': 'schrat',
    'sch1': 'schrat',
    'sch2': 'sch2',
    'ends': 'end',
    'materialastm': 'materialastm',
    'material': 'material',
    'astm': 'astm',
    'construction': 'construction',
    'constr': 'construction',
    'designstandard': 'designstandard',
    'specialrequirements': 'specialrequirements',
  };

  private normalizeFieldName(propName: string): string {
    const n = propName.toLowerCase().replace(/[\s_\/\-]/g, '');
    return this.FIELD_KEY_MAP[n] ?? n;
  }

  private getCanonicalPropertyName(fieldName: string): string | undefined {
    return this.canonicalPropertyNamesByField.get(fieldName.toLowerCase());
  }

  isFieldVisible(fieldName: string): boolean {
    if (!this.selectedGroup) return false;
    if (this.isSpecialRequirementsDisabledGroup(this.selectedGroup) && fieldName.toLowerCase() === 'specialrequirements') {
      return false;
    }
    return this.visibleFieldNames.has(fieldName.toLowerCase());
  }

  hasDescriptionDropdown(): boolean {
    return this.GROUPS_WITH_DESCRIPTION_DROPDOWN.includes(this.selectedGroup?.toLowerCase());
  }

  private isSpecialRequirementsDisabledGroup(group: string | null | undefined): boolean {
    const normalizedGroup: string = (group ?? '').trim().toLowerCase();
    return normalizedGroup === 'gasket'
      || normalizedGroup === 'gaskets'
      || normalizedGroup === 'bolt'
      || normalizedGroup === 'bolts';
  }

  private loadDescriptionsForGroup(group: string): void {
    this._esraPipingAPIClient.getDescriptionsByGroup(group)
      .pipe(first())
      .subscribe({
        next: (descriptions) => {
          this.descriptionOptions = descriptions ?? [];
        },
        error: (err) => console.log('Error loading descriptions for group: ' + err)
      });
  }

  private loadRequirementsForGroup(group: string): void {
    if (this.isSpecialRequirementsDisabledGroup(group)) {
      this.filteredRequirementsInit = [];
      this.requirementsOptions$.next([]);
      const requirementsControl: AbstractControl = this.addFormGroup.get('requirements');
      requirementsControl.setValue('');
      requirementsControl.disable({ emitEvent: false });
      this.componentModel.specialRequirements = null;
      return;
    }

    this.addFormGroup.get('requirements').enable({ emitEvent: false });
    this._esraPipingAPIClient.getAllComponentProperties(group, undefined, true, false, undefined)
      .pipe(first())
      .subscribe({
        next: (props: ComponentPropertyViewModel[]) => {
          const requirementValues: string[] = (props ?? [])
            .filter((property: ComponentPropertyViewModel) => this.normalizeFieldName(property?.name ?? '') === 'specialrequirements')
            .map((property: ComponentPropertyViewModel) => property.value)
            .filter((value: string) => !!value);

          this.filteredRequirementsInit = this.distinctSortedValues(requirementValues);
          this.requirementsOptions$.next(this.filteredRequirementsInit);
        },
        error: (err) => console.log('Error loading special requirements for group: ' + err)
      });
  }

  /**
   * Loads the group's combined Material/ASTM dictionary values and decodes them into pairs.
   * Both autocompletes are sourced from these pairs so each side can be narrowed by the other,
   * and the validators only accept values that exist for the selected group.
   */
  private loadMaterialAstmForGroup(group: string): void {
    this._esraPipingAPIClient.getAllComponentProperties(group, undefined, true, false, undefined)
      .pipe(first())
      .subscribe({
        next: (props: ComponentPropertyViewModel[]) => {
          const pairs: MaterialAstmValue[] = (props ?? [])
            .filter((property: ComponentPropertyViewModel) => {
              const normalizedName: string = this.normalizeFieldName(property?.name ?? '');
              return normalizedName === this.normalizeFieldName(MATERIAL_ASTM_PROPERTY_NAME);
            })
            .map((property: ComponentPropertyViewModel) => deserializeMaterialAstm(property.value));

          this.materialAstmPairs = pairs;
          this.materialAstmPairs$.next(pairs);
          this.revalidateMaterialAstm();
        },
        error: (err) => console.log('Error loading material/ASTM values for group: ' + err)
      });
  }

  /** Materials available for the chosen ASTM; unnarrowed while the ASTM box holds partial text. */
  private materialOptionsFor(pairs: MaterialAstmValue[], selectedAstm: string): string[] {
    return this.distinctSortedValues(this.pairsMatching(pairs, selectedAstm, pair => pair.astm).map(pair => pair.material));
  }

  /** ASTMs available for the chosen material; unnarrowed while the material box holds partial text. */
  private astmOptionsFor(pairs: MaterialAstmValue[], selectedMaterial: string): string[] {
    return this.distinctSortedValues(this.pairsMatching(pairs, selectedMaterial, pair => pair.material).map(pair => pair.astm));
  }

  /**
   * Validates a Material/ASTM control against the list the user can actually pick from -- the
   * one already narrowed by the other field -- so a value that exists elsewhere in the group but
   * does not pair with the current counterpart is rejected.
   * @param counterpartSide Reads the other field's value out of a pair.
   * @param ownSide Reads this field's value out of a pair.
   * @param counterpartControlName Form control holding the counterpart value.
   */
  private materialAstmPairValidator(
    counterpartSide: (pair: MaterialAstmValue) => string,
    ownSide: (pair: MaterialAstmValue) => string,
    counterpartControlName: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null;
      }

      const counterpartValue: string = this.addFormGroup?.get(counterpartControlName)?.value ?? '';
      const allowedValues: string[] = this.distinctSortedValues(
        this.pairsMatching(this.materialAstmPairs, counterpartValue, counterpartSide).map(ownSide));

      return allowedValues.includes(control.value) ? null : { 'invalidAutocompleteString': { value: control.value } };
    };
  }

  /** Re-checks both controls, since each one's valid values depend on the other's current value. */
  private revalidateMaterialAstm(): void {
    this.addFormGroup.get('material').updateValueAndValidity({ emitEvent: false });
    this.addFormGroup.get('astm').updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Narrows `pairs` to those whose counterpart side equals `selectedValue`.
   * Returns every pair while the user is still typing (no pair matches yet), so a partially
   * typed value never empties the opposite list.
   */
  private pairsMatching(pairs: MaterialAstmValue[], selectedValue: string, side: (pair: MaterialAstmValue) => string): MaterialAstmValue[] {
    const value: string = selectedValue?.trim() ?? '';
    if (!value) {
      return pairs;
    }

    const matches: MaterialAstmValue[] = pairs.filter(pair => side(pair) === value);
    return matches.length > 0 ? matches : pairs;
  }

  private distinctSortedValues(values: string[]): string[] {
    const uniqueValues: Set<string> = new Set<string>(values.filter(value => !!value?.trim()));
    return Array.from(uniqueValues).sort((first: string, second: string) => first.localeCompare(second));
  }

  private loadTypesForGroup(group: string): void {
    this._esraPipingAPIClient.getAllComponentTypesByGroupName(group)
      .pipe(first())
      .subscribe({
        next: (types) => {
          this.filteredTypeInit = types ?? [];
          this.addFormGroup.get('type').setValidators(
            [this.autocompleteStringValidator(this.filteredTypeInit), Validators.required]
          );
          this.addFormGroup.get('type').updateValueAndValidity();
          this.typeOptions$.next(this.filteredTypeInit);
        },
        error: (err) => console.log('Error loading types for group: ' + err)
      });
  }

  private loadVisibleFieldsForGroup(group: string): void {
    this._esraPipingAPIClient.getAttributeNamesByGroup(group)
      .pipe(first())
      .subscribe({
        next: (attributeNames: string[]) => {
          const canonicalNames: string[] = (attributeNames ?? []).filter((attributeName: string) => !!attributeName);
          const normalizedFieldNames: Set<string> = new Set<string>(
            canonicalNames.map((attributeName: string) => this.normalizeFieldName(attributeName))
          );

          this.canonicalPropertyNamesByField = new Map<string, string>();
          canonicalNames.forEach((attributeName: string) => {
            const normalizedFieldName: string = this.normalizeFieldName(attributeName);
            if (!this.canonicalPropertyNamesByField.has(normalizedFieldName)) {
              this.canonicalPropertyNamesByField.set(normalizedFieldName, attributeName);
            }
          });

          if (normalizedFieldNames.has('materialastm')) {
            normalizedFieldNames.add('material');
            normalizedFieldNames.add('astm');
          }

          this.visibleFieldNames = normalizedFieldNames;
          this.selectedGroup = group;

          this.loadAutocompleteOptionsForGroup(group);
          this.loadRequirementsForGroup(group);
          this.loadMaterialAstmForGroup(group);
        },
        error: () => {
          this.visibleFieldNames = new Set<string>();
          this.canonicalPropertyNamesByField = new Map<string, string>();
          this.selectedGroup = group;
        }
      });
  }

  private loadAutocompleteOptionsForGroup(group: string): void {
    this.loadOptionsForField(group, 'end', (options: string[]) => {
      this.filteredEndsInit = [''].concat(options);
      this.addFormGroup.get('ends').setValidators([this.autocompleteStringValidator(this.filteredEndsInit)]);
      const currentEnd: string = this.addFormGroup.get('ends').value ?? '';
      if (!!currentEnd && !this.filteredEndsInit.includes(currentEnd)) {
        this.addFormGroup.get('ends').setValue('');
      }
      this.addFormGroup.get('ends').updateValueAndValidity({ emitEvent: false });
    });

    this.loadOptionsForField(group, 'construction', (options: string[]) => {
      this.filteredCONSTInit = [''].concat(options);
      this.addFormGroup.get('const').setValidators([this.autocompleteStringValidator(this.filteredCONSTInit)]);
      const currentConstruction: string = this.addFormGroup.get('const').value ?? '';
      if (!!currentConstruction && !this.filteredCONSTInit.includes(currentConstruction)) {
        this.addFormGroup.get('const').setValue('');
      }
      this.addFormGroup.get('const').updateValueAndValidity({ emitEvent: false });
    });

    this.loadOptionsForField(group, 'designstandard', (options: string[]) => {
      this.filteredSTDInit = [''].concat(options);
      this.addFormGroup.get('design').setValidators([this.autocompleteStringValidator(this.filteredSTDInit)]);
      const currentDesignStandard: string = this.addFormGroup.get('design').value ?? '';
      if (!!currentDesignStandard && !this.filteredSTDInit.includes(currentDesignStandard)) {
        this.addFormGroup.get('design').setValue('');
      }
      this.addFormGroup.get('design').updateValueAndValidity({ emitEvent: false });
    });
  }

  private clearComponentPropertyInputsForGroupChange(): void {
    this.addFormGroup.get('ends').setValue('');
    this.addFormGroup.get('material').setValue('');
    this.addFormGroup.get('astm').setValue('');
    this.addFormGroup.get('const').setValue('');
    this.addFormGroup.get('design').setValue('');
    this.addFormGroup.get('requirements').setValue('');
    this.addFormGroup.get('description').setValue('');

    this.componentModel.end = '';
    this.componentModel.material = '';
    this.componentModel.astm = '';
    this.componentModel.construction = '';
    this.componentModel.designStandard = '';
    this.componentModel.specialRequirements = '';
    this.componentModel.description = '';
  }

  private loadOptionsForField(group: string, fieldKey: string, onLoaded: (options: string[]) => void): void {
    this._esraPipingAPIClient.getAllComponentProperties(group, undefined, true, false, undefined)
      .pipe(first())
      .subscribe({
        next: (properties: ComponentPropertyViewModel[]) => {
          const normalizedFieldKey: string = fieldKey.toLowerCase();
          const options: string[] = this.distinctSortedValues(
            (properties ?? [])
              .filter((property: ComponentPropertyViewModel) => this.normalizeFieldName(property?.name ?? '') === normalizedFieldKey)
              .map((property: ComponentPropertyViewModel) => property.value)
          );
          onLoaded(options);
        },
        error: () => {
          onLoaded([]);
        }
      });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  onEndsFocus(): void {
    this.endsOpen.next();
  }

  save(): void {
    if (!this.addFormGroup.valid) {
      this.addFormGroup.markAllAsTouched();
      this._snackBarService.showSnackBar(true, 'Please correct errors in selected fields.');
      return;
    }

    if (this.isSpecialRequirementsDisabledGroup(this.componentModel?.group)) {
      this.componentModel.specialRequirements = null;
    }

    this.dialogRef.close(this.componentModel);
  }

  autocompleteStringValidator(validOptions: Array<string>): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null;
      }
      if (validOptions.indexOf(control.value) !== -1) {
        return null;
      }
      return { 'invalidAutocompleteString': { value: control.value } }
    }
  }

  private _filterOptions(label: string, collection: Array<string>): string[] {
    if (label === '') {
      return collection.slice()
    }
    const filterValue = label.toLowerCase()
    return collection.filter(option => option.toLowerCase().includes(filterValue))
  }
}
